import {useParams} from "react-router-dom";
import {
  ApprovalStatus,
  ApprovalStepStatus,
  useGetZyusetuByApprovalExecutionIdQuery,
  ZyusetuFieldValueAnalyzedPayloadFragmentFragment,
  ZyusetuFragmentFragment
} from "@/graphql/graphql.ts";
import {LoadingPage} from "@/pages/LoadingPage.tsx";
import {NotFoundPage} from "@/pages/NotFoundPage.tsx";
import {useCallback, useContext, useEffect, useMemo, useState} from "react";
import {FileTypeLabel, FILE_TYPE_COLORS} from "@/types/FileType.tsx";
import {HighlightedField, SetHighlightedField} from "@/hooks/use-highlight-field.ts";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid2,
  List,
  Stack,
  Typography
} from "@mui/material";
import {FileInputListItem} from "@/components/FileInputListItem.tsx";
import {ChevronDown} from "lucide-react";
import {usePreloadFiles} from "@/hooks/use-preload-files.ts";
import {CrossSectionFieldsProvider} from "@/hooks/CrossSectionFieldsProvider.tsx";
import {ZyusetuEditor} from "@/components/pages/Zyusetu/ZyusetuEditor.tsx";
import {ZyusetuEditType} from "@/types/ZyusetuEditType.ts";
import {DataGridItemVertical} from "@/components/dataDisplay/DataGridItemVertical.tsx";
import {ApprovalStatusChip} from "@/components/pages/Approval/ApprovalStatusChip.tsx";
import {PdfViewer} from "@/components/PdfViewer.tsx";
import {useUserData} from "@/hooks/use-user-data-provider.ts";
import {useAtom} from "jotai/index";
import {ZyusetuAtom} from "@/plugin/jotai-wrapper.ts";

export function ZyusetuReviewPage() {
  const {approvalStepExecutionId} = useParams<{ approvalStepExecutionId: string }>();
  const {data, isLoading} = useGetZyusetuByApprovalExecutionIdQuery({
    executionId: approvalStepExecutionId!
  }, {
    refetchOnMount: true,
    retry: 0,
  })
  
  const [zyusetu, setZyusetu] = useAtom(ZyusetuAtom);
  
  useEffect(() => {
    setZyusetu(() => data?.getZyusetuByStepExecutionId);
  }, [setZyusetu, data?.getZyusetuByStepExecutionId]);
  
  const files = useMemo(() => {
    return zyusetu?.files ?? [];
  }, [zyusetu?.files]);
  
  const setHighlightedField = useContext(SetHighlightedField);
  
  const onFieldFocus = useCallback(
    (value: ZyusetuFieldValueAnalyzedPayloadFragmentFragment) => {
      const file = files.find((file) => file.id === value.analyzedFileId);
      if (!file) {
        return;
      }
      if (!value?.boundingRegions && !value.pageAnchor) {
        return;
      }
      setHighlightedField({field: value, file});
    },
    [files, setHighlightedField],
  );
  
  const userData = useUserData()
  
  const isMyTern = useMemo(() => {
    const currentStepIndex = zyusetu?.approvalFlow?.steps?.findIndex(step =>
      step.stepExecution.some(exec => exec.id === approvalStepExecutionId)
    ) ?? -1;
    
    if (currentStepIndex === -1) {
      return false
    }
    
    const currentExecution = zyusetu?.approvalFlow?.steps[currentStepIndex]?.stepExecution.find(exec => exec.id === approvalStepExecutionId);
    
    return currentExecution?.userId === userData.user?.id && currentExecution?.status === ApprovalStepStatus.PendingApproval
  }, [approvalStepExecutionId, userData.user?.id, zyusetu?.approvalFlow?.steps])
  
  usePreloadFiles(files);
  
  if (isLoading || zyusetu?.id === "") {
    return <LoadingPage/>;
  }
  if (!zyusetu) {
    return <NotFoundPage/>
  }
  
  return (
    <>
      <Grid2 container spacing={3}>
        <Grid2 size={7}>
          <CrossSectionFieldsProvider>
            <ZyusetuEditor
              approvalStepExecutionId={approvalStepExecutionId}
              editType={zyusetu.approvalStatus === ApprovalStatus.InReview ? isMyTern ? ZyusetuEditType.Review : ZyusetuEditType.ReadOnly : ZyusetuEditType.ReadOnly}
              onFieldFocus={onFieldFocus}
            />
          </CrossSectionFieldsProvider>
        </Grid2>
        <Grid2 size={5}>
          <Card sx={{position: "sticky", top: "20px", overflow: 'scroll', maxHeight: "100vh", overflowY: 'auto'}}>
            <CardContent>
              <FileAddViewer
                zyusetu={zyusetu}/>
            </CardContent>
          </Card>
        </Grid2>
      </Grid2>
    </>
  );
}

function FileAddViewer(
  {zyusetu}: { zyusetu: ZyusetuFragmentFragment },
) {
  
  const zyusetuId = zyusetu?.id
  
  const filesForInputList = useMemo(() => {
    return (
      zyusetu?.template.requiredFileTypes.concat(zyusetu?.template?.optionalFileTypes).map((type) => {
        return {
          type,
          name: FileTypeLabel[type],
          file: zyusetu?.files.find(
            (file) => file.fileType === type,
          ),
        };
      }) ?? []
    );
  }, [zyusetu?.files, zyusetu?.template?.optionalFileTypes, zyusetu?.template.requiredFileTypes]);
  
  const [expanded, setExpanded] = useState(true);
  
  const setHighlightedField = useContext(SetHighlightedField);
  
  return (<>
    <Accordion defaultExpanded elevation={0} expanded={expanded}>
      <AccordionSummary
        tabIndex={-1}
        expandIcon={<ChevronDown/>}
        onClick={() => setExpanded((prev) => !prev)}
      >
        <Typography>ファイルを追加する</Typography>
      </AccordionSummary>
      <AccordionDetails>
        
        <List>
          <Divider/>
          {filesForInputList.map((file, idx) => (
            <>
              <FileInputListItem
                key={file.name}
                fileType={file.type}
                title={file.name}
                file={file.file}
                onFileNameClick={(file) => {
                  setHighlightedField({
                    field: null,
                    file,
                  })
                }}
                zyusetuId={zyusetuId!}
              />
              {idx !== filesForInputList.length ? <Divider/> : null}
            </>
          ))}
        </List>
        <Grid2 container sx={{mb: 2}}>
          <DataGridItemVertical
            labelWidth={4}
            label={"テンプレート名"}
          >
            {zyusetu.template.title}
          </DataGridItemVertical>
          <DataGridItemVertical
            labelWidth={4}
            label={"承認ステータス"}
          >
            <ApprovalStatusChip status={zyusetu.approvalStatus}/>
          </DataGridItemVertical>
        </Grid2>
      </AccordionDetails>
    </Accordion>
    <FileOverview/>
  </>)
}

function FileOverview() {
  const selectedFile = useContext(HighlightedField)
  return (
    selectedFile?.file ? <Box key={"pdf-viewer"}>
      <Stack
        my={1}
        direction={"row"}
        alignItems={"center"}
        spacing={1}
      >
        <Chip
          sx={{
            backgroundColor: FILE_TYPE_COLORS[selectedFile?.file.fileType],
            color: 'rgba(0, 0, 0, 0.7)',
            fontWeight: 'medium'
          }}
          label={FileTypeLabel[selectedFile?.file.fileType]}
        />
        <Typography>{selectedFile?.file.title}</Typography>
      </Stack>
      <PdfViewer/>
    </Box> : <></>
  )
}
